import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { User as UserIcon, RefreshCw } from "@/components/icons";

const ROLE_LABEL = {
  doctor: "Doctor",
  secretary: "Secretaria",
  admin: "Administrador",
  pharmacy: "Farmacia",
  support: "Soporte",
};

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-DO", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

export default function Presence() {
  const [date, setDate] = useState(todayISO());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/presence-log", {
        params: { date },
      });
      setEvents(data.events || []);
    } catch (e) {
      toast.error("No se pudo cargar el historial de presencia");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Group by user: show first login and last activity per user, plus event list
  const perUser = useMemo(() => {
    const map = new Map();
    // events are returned desc; iterate reversed to get chronological order
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      const key = e.user_id;
      if (!map.has(key)) {
        map.set(key, {
          user_id: e.user_id,
          user_name: e.user_name,
          user_email: e.user_email,
          user_role: e.user_role,
          user_specialty: e.user_specialty,
          user_photo_url: e.user_photo_url,
          events: [],
        });
      }
      map.get(key).events.push(e);
    }
    return Array.from(map.values()).sort((a, b) => {
      const aLast = a.events[a.events.length - 1]?.at || "";
      const bLast = b.events[b.events.length - 1]?.at || "";
      return bLast.localeCompare(aLast);
    });
  }, [events]);

  const totalLogins = events.filter((e) => e.event === "login").length;
  const uniqueUsers = perUser.length;

  const initials = (name) =>
    (name || "?")
      .split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <div className="space-y-6" data-testid="admin-presence">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-black tracking-tight">
            Presencia
          </h1>
          <p className="text-sm text-black/60">
            Historial de conexiones y desconexiones por día.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={todayISO()}
            data-testid="presence-date-input"
            className="px-3 py-2 rounded-xl border border-black/[0.08] bg-white text-sm font-medium text-black focus:outline-none focus:border-black/30"
          />
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="presence-refresh"
            className="w-10 h-10 rounded-xl bg-white border border-black/[0.08] flex items-center justify-center hover:bg-black/[0.03] transition disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              strokeWidth={1.75}
              weight="regular"
            />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div
          className="bg-white rounded-2xl border border-black/[0.06] p-4"
          data-testid="presence-kpi-logins"
        >
          <div className="text-[11px] uppercase tracking-wider text-black/50 font-semibold">
            Inicios de sesión
          </div>
          <div className="text-3xl font-extrabold text-black mt-1">
            {totalLogins}
          </div>
        </div>
        <div
          className="bg-white rounded-2xl border border-black/[0.06] p-4"
          data-testid="presence-kpi-users"
        >
          <div className="text-[11px] uppercase tracking-wider text-black/50 font-semibold">
            Usuarios activos
          </div>
          <div className="text-3xl font-extrabold text-black mt-1">
            {uniqueUsers}
          </div>
        </div>
        <div
          className="bg-white rounded-2xl border border-black/[0.06] p-4"
          data-testid="presence-kpi-events"
        >
          <div className="text-[11px] uppercase tracking-wider text-black/50 font-semibold">
            Eventos totales
          </div>
          <div className="text-3xl font-extrabold text-black mt-1">
            {events.length}
          </div>
        </div>
      </div>

      {/* Timeline por usuario */}
      {perUser.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/[0.06] p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-black/[0.04] flex items-center justify-center text-black/30">
            <UserIcon className="w-6 h-6" strokeWidth={1.5} weight="regular" />
          </div>
          <div className="mt-3 text-[14px] text-black/60">
            {loading
              ? "Cargando..."
              : "Sin actividad registrada para este día."}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.06]">
          {perUser.map((u) => (
            <div
              key={u.user_id}
              data-testid={`presence-row-${u.user_id}`}
              className="p-4 flex items-start gap-4"
            >
              <span className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex items-center justify-center text-white font-bold text-[13px]">
                {u.user_photo_url ? (
                  <img
                    src={u.user_photo_url}
                    alt={u.user_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  initials(u.user_name)
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] text-black font-semibold truncate">
                  {u.user_name}
                  <span className="ml-2 text-[11px] font-medium text-black/50">
                    {ROLE_LABEL[u.user_role] || u.user_role}
                    {u.user_specialty ? ` · ${u.user_specialty}` : ""}
                  </span>
                </div>
                <div className="text-[12px] text-black/50 truncate">
                  {u.user_email}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {u.events.map((e) => (
                    <span
                      key={e.id}
                      data-testid={`presence-event-${e.id}`}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold ${
                        e.event === "login"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                      }`}
                      title={e.at}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          e.event === "login"
                            ? "bg-[#22C55E]"
                            : "bg-[#EF4444]"
                        }`}
                      />
                      {e.event === "login" ? "Entró" : "Salió"}
                      <span className="font-mono text-black/60 font-normal">
                        {formatTime(e.at)}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
